import React, { FC, useContext, useEffect, useState } from 'react';
import SkillCard from './skill-card.component';
import { Icons, Images } from '../../../../assets';
import { ButtonLoader } from '../../../../components';
import { AccountContext, AccountContextInterface } from '../../../../contexts';
import { UserSkill } from '../../../../models/skill.model';
import { Tab } from '../../../../types';

interface SkillSectionProps {
  accountId: string;
  onSkillClick: (skill: UserSkill, type: Tab.Creds | Tab.Reccos) => void;
}

const SkillSection: FC<SkillSectionProps> = ({ accountId, onSkillClick }) => {
  const { accountService } = useContext<AccountContextInterface>(AccountContext);
  const [skills, setSkills] = useState<UserSkill[]>([]);
  const [hasMore, setHasMore] = useState<boolean>(true);
  const [loading, setLoading] = useState<boolean>(false);
  const [page, setPage] = useState<number>(1);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const { data } = await accountService.getUserSkills(accountId, page, 10);
        if (data) {
          if (page === 1) {
            setSkills(data);
          } else {
            setSkills(prevSkills => [...prevSkills, ...data]);
          }
          if (data.length < 10) {
            setHasMore(false);
          } else {
            setHasMore(true);
          }
        }
        setLoading(false);
      } catch (error) {
        setLoading(false);
      }
    };

    fetchData();
  }, [accountId, accountService, page]);

  const handleLoadMore = () => {
    if (hasMore && !loading) {
      setPage(prevPage => prevPage + 1);
    }
  };

  return (
    <div className="bg-white flex flex-col gap-6 mb-4">
      <div className="flex gap-2 items-center">
        <img src={Icons.SKILL_ICON} alt="skills" className="h-6 w-6" />
        <span className="font-semibold text-lg text-[#1A1A1A]">Skills</span>
      </div>
      {loading && skills.length === 0 && (
        <div className="flex items-center justify-center py-10">
          <ButtonLoader />
        </div>
      )}
      {!loading && skills.length === 0 && (
        <div className="flex flex-col gap-4 items-center justify-center py-10">
          <img src={Images.NO_ACTIVITY} alt="No Skills" className="h-32 w-32" />
          <p>No Skills</p>
        </div>
      )}
      {skills.length > 0 && (
        <div className="flex flex-col gap-6">
          <div className="gap-6 grid grid-cols-1">
            {skills.map(skill => (
              <SkillCard key={skill.id} skill={skill} onSkillClick={onSkillClick} />
            ))}
          </div>
          {hasMore && (
            <button
              onClick={handleLoadMore}
              disabled={loading}
              className="bg-blue-500 px-4 py-2 rounded-lg text-white disabled:bg-gray-300">
              {loading ? <ButtonLoader /> : 'View More'}
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default SkillSection;
